import { getBaseVehicle, getDetailedDeliveryInfo, getGenericPremium, getPaymentInfo } from 'apis/vehicleApis'
import {
  BidAmountTypeOptions,
  OTHER_TRANSPORT_PROVIDER_ID,
  SELF_ARRANGED_TRANSPORT_PROVIDER_ID
} from 'common/constants'
import { SlideOutHeading } from 'components/SlideOut/SlideOutHeading'
import { SlideOutDropdown } from 'components/SlideOut/SlideOutDropdown'
import { useState, useMemo, useEffect, useCallback } from 'react'
import { useBiddingSidePanel } from 'store/useBiddingSidePanelStore'
import { IOption, IDropdownData } from 'types/baseTypes'
import { GetBaseVehicleRequest, IDeliveryQuote, IVehicleFeeRequest } from 'types/vehicleTypes'
import shallow from 'zustand/shallow'
import { StyledSlideOutElement } from 'components/SlideOut/SlideOut.styled'
import { SlideOutBidAmountInput } from 'components/SlideOut/SlideOutBidAmountInput'
import { StyledSlideOutBidAmountInput } from 'components/SlideOut/SlideOutBidAmountInput.styled'
import { Form } from 'react-bootstrap'
import { useGlobalStore } from 'store/useGlobalStore'
import { SlideOutPricingDetails } from 'components/SlideOut/SlideOutPricingDetails'
import { useFormikContext } from 'formik'
import { BidAgainFormValues } from 'types/formikTypes'
import { useVehicleStore } from 'store/useVehicleStore'
import { debounce } from 'lodash'
import { useSidePanelRealTimeUpdateDealer } from 'hooks/useSidePanelRealTimeUpdateDealer'
import { IAccountInfo } from 'types/accountTypes'
import { CustomRadioButton } from 'components/DeliveryChargeCard/CustomRadioButton'
import { normalizeCurrencySymbol } from 'utils/currencyUtils'

interface IBiddingSidePanelFormProps {
  isDealerShipSelectable: boolean
  setFetchLoading: (loading: boolean) => void
  openTransportationSlideOut: () => void
  closeTransportationSlideOut: () => void
  isBidSuccess: boolean
}

export const BiddingSidePanelFormContent: React.FC<IBiddingSidePanelFormProps> = ({
  isDealerShipSelectable,
  setFetchLoading,
  openTransportationSlideOut,
  closeTransportationSlideOut,
  isBidSuccess
}) => {
  const formik = useFormikContext<BidAgainFormValues>()
  const biddingVehicleInstanceId = useBiddingSidePanel((state) => state.biddingVehicleInstanceId)
  const biddingVehicle = useVehicleStore(
    (state) => state.vehicles?.find((vehicleData) => vehicleData.vehicle.InstanceID === biddingVehicleInstanceId),
    shallow
  )
  const updateVehicleData = useVehicleStore((state) => state.updateVehicleData)
  const {
    deliveryInformation,
    setDeliveryInformation,
    selectedDeliveryPrice,
    setSelectedDeliveryPrice,
    otherChildId,
    setOtherChildId,
    isOther,
    setIsOther,
    isBuyItNow,
    isPurchaseExclusive,
    hasTransportationSelected,
    setHasTransportationSelected,
    setHasPaymentMethodSelected
  } = useBiddingSidePanel(
    (s) => ({
      deliveryInformation: s.deliveryInformation,
      setDeliveryInformation: s.setDeliveryInformation,
      selectedDeliveryPrice: s.selectedDeliveryPrice,
      setSelectedDeliveryPrice: s.setSelectedDeliveryPrice,
      otherChildId: s.otherChildId,
      setOtherChildId: s.setOtherChildId,
      isOther: s.isOther,
      setIsOther: s.setIsOther,
      isBuyItNow: s.isBuyItNow,
      isPurchaseExclusive: s.isPurchaseExclusive,
      hasTransportationSelected: s.hasTransportationSelected,
      setHasTransportationSelected: s.setHasTransportationSelected,
      setHasPaymentMethodSelected: s.setHasPaymentMethodSelected
    }),
    shallow
  )

  const { AssociatedBuyers, CurrentBuyerID } = useGlobalStore((state) => state.userClaims)
  const [feeLoading, setFeeLoading] = useState(false)
  const [buyerFee, setBuyerFee] = useState(0)
  const [buyerFeeString, setBuyerFeeString] = useState('N/A')
  const [hasAnyChargeRange, setHasAnyChargeRange] = useState(false)
  const [vehicleInfo, setVehicleInfo] = useState(biddingVehicle?.vehicle)
  const [isFetchingDeliveryInfo, setIsFetchingDeliveryInfo] = useState(false)

  const [isExistOtherTransportProvider, setIsExistOtherTransportProvider] = useState(false)
  const [isDealerChangeInProgress, setIsDealerChangeInProgress] = useState(false)

  const dealershipOptions: IOption<IDropdownData>[] = useMemo(
    () =>
      biddingVehicle?.auctionInfo?.SalesSessionStepBuyerTypeIds.length > 0
        ? AssociatedBuyers.filter((dealership) =>
            biddingVehicle?.auctionInfo?.SalesSessionStepBuyerTypeIds.some(
              (sessionStepBuyerType) => sessionStepBuyerType === dealership.BuyerTypeID
            )
          ).map((dealership) => ({
            item: dealership,
            label: dealership.Description,
            value: dealership.ID
          }))
        : AssociatedBuyers.map((dealership) => ({
            item: dealership,
            label: dealership.Description,
            value: dealership.ID
          })),

    [AssociatedBuyers, biddingVehicle?.auctionInfo?.SalesSessionStepBuyerTypeIds]
  )

  const buyerAccountOptions: IOption<IDropdownData>[] = useMemo(
    () =>
      deliveryInformation?.CurrentBuyerAlternateIdentifiers?.map((acc: IAccountInfo) => ({
        item: acc,
        label: acc.Description,
        value: acc.Description
      })),
    [deliveryInformation?.CurrentBuyerAlternateIdentifiers]
  )

  const paymentTypes: IOption<IDropdownData>[] = useMemo(() => {
    return deliveryInformation?.PaymentTypes?.map((paymentType) => ({
      item: paymentType,
      label: paymentType.Description,
      value: paymentType.ID
    }))
  }, [deliveryInformation?.PaymentTypes])

  const selfArrangeTransportationProvider: IDropdownData = useMemo(
    () =>
      deliveryInformation?.TransportProviders.find((provider) => provider.ID === SELF_ARRANGED_TRANSPORT_PROVIDER_ID),
    [deliveryInformation?.TransportProviders]
  )

  const estimatedGrossPrice = useMemo(() => {
    if (!biddingVehicle?.auctionInfo && !isPurchaseExclusive) {
      return undefined
    }

    const totalCharges =
      vehicleInfo?.Charges?.reduce((accumulator, currentValue) => accumulator + currentValue.Price, 0) || 0

    const promotions =
      vehicleInfo?.Incentives?.reduce((accumulator, currentValue) => accumulator + currentValue.Amount, 0) || 0

    let total = 0
    const deliveryPrice = selectedDeliveryPrice || 0 // for now this almost always 0 because we default to self-arranged

    // for now this fee only applicable for Nissan
    const titleOptionFee = deliveryInformation?.TitleDeliveryCharge || 0
    const nextBidAmount = isBuyItNow
      ? biddingVehicle?.vehicle?.BuyItNowPrice
      : isPurchaseExclusive
      ? biddingVehicle?.vehicle?.FinalPrice
      : biddingVehicle?.auctionInfo?.NextBidAmount
    if (vehicleInfo?.IsIncentiveAccumulate) {
      total = buyerFee + nextBidAmount + deliveryPrice + titleOptionFee + totalCharges
    } else {
      total = buyerFee + nextBidAmount + deliveryPrice + titleOptionFee + totalCharges + promotions
    }
    if (isPurchaseExclusive) {
      total = Number(vehicleInfo?.GrossPriceString?.replace(/[^0-9.]+/g, '')) || 0
    }
    return total
  }, [
    biddingVehicle?.auctionInfo,
    biddingVehicle?.vehicle,
    isPurchaseExclusive,
    isBuyItNow,
    buyerFee,
    deliveryInformation?.TitleDeliveryCharge,
    vehicleInfo?.Charges,
    vehicleInfo?.Incentives,
    vehicleInfo?.IsIncentiveAccumulate,
    selectedDeliveryPrice,
    vehicleInfo?.GrossPriceString
  ])

  const fetchDetailedDeliveryInfo = useCallback(
    async (biddingDealerID?: number) => {
      closeTransportationSlideOut()

      if (biddingVehicle?.vehicle && (biddingVehicle?.auctionInfo || isPurchaseExclusive)) {
        const vehicle = biddingVehicle.vehicle

        const salePrice = biddingVehicle.auctionInfo?.NextBidAmount || vehicle.FinalPrice

        const deliveryQuote: IDeliveryQuote = {
          ChannelID: vehicle.SaleChannelID,
          InterestID: vehicle.OfferID,
          InterestStatusID: vehicle.OfferStatusID,
          LocationID: vehicle.LocationID,
          PaymentTypeID: 0,
          SalePrice: salePrice,
          SalesSessionStepId: vehicle.SalesSessionStepID,
          VehicleInstanceID: vehicle.InstanceID
        }
        try {
          setIsFetchingDeliveryInfo(true)
          setIsOther(false)
          const deliveryInformation = await getDetailedDeliveryInfo(
            deliveryQuote,
            biddingDealerID || biddingVehicle.auctionInfo?.BuyerID
          )
          formik.setFieldValue('PaymentTypeID', deliveryInformation.PaymentTypeID, false)
          setHasPaymentMethodSelected(deliveryInformation.PaymentTypeID !== 0)
          setDeliveryInformation(deliveryInformation)
          setBuyerFee(deliveryInformation.BuyerFee)
          setBuyerFeeString(deliveryInformation.BuyerFeeString)
          setHasAnyChargeRange(deliveryInformation.HasAnyChargeRange)
          const hasOtherTransportProvider =
            deliveryInformation?.TransportProviders?.some(
              (provider) => provider.ID !== SELF_ARRANGED_TRANSPORT_PROVIDER_ID
            ) || false
          setIsExistOtherTransportProvider(hasOtherTransportProvider)
          if (hasOtherTransportProvider) {
            if (
              deliveryInformation?.HasQuote &&
              (biddingVehicle?.auctionInfo?.HasPreviousBid
                ? biddingVehicle?.auctionInfo?.BuyerID === CurrentBuyerID
                : false)
            ) {
              const newTransportationID =
                deliveryInformation?.TransportCompanyID !== SELF_ARRANGED_TRANSPORT_PROVIDER_ID
                  ? OTHER_TRANSPORT_PROVIDER_ID
                  : SELF_ARRANGED_TRANSPORT_PROVIDER_ID

              setIsOther(true)
              setHasTransportationSelected(true)
              if (newTransportationID === OTHER_TRANSPORT_PROVIDER_ID) {
                const newOtherChildId = deliveryInformation?.TransportCompanyID
                setOtherChildId(newOtherChildId)
                formik.setFieldValue('TransportCompanyID', OTHER_TRANSPORT_PROVIDER_ID)
                openTransportationSlideOut()
              }

              const selectedQuote = deliveryInformation.VehicleDeliveryQuotations

              const quotePrice = selectedQuote
                ? selectedQuote.DiscountedPrice
                : deliveryInformation.HasQuote
                ? deliveryInformation.DiscountedPrice
                : null
              setSelectedDeliveryPrice(quotePrice as number)
            } else {
              setIsOther(false)
              closeTransportationSlideOut()
            }
          }
        } catch (error) {
          console.error('Error fetching delivery information:', error)
        } finally {
          setIsFetchingDeliveryInfo(false)
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [biddingVehicle?.auctionInfo, biddingVehicle?.vehicle]
  )

  const fetchPaymentInfo = async (vehicleInstanceID: number) => {
    try {
      const paymentInfo = await getPaymentInfo(vehicleInstanceID)
      if (paymentInfo.PaymentTypes.length > 0) {
        formik.setFieldValue('PaymentTypeID', paymentInfo.PaymentTypes[1].ID, false)
        setHasPaymentMethodSelected(paymentInfo.PaymentTypes[1].ID !== 0)
        setDeliveryInformation(paymentInfo)
      }
    } catch (error) {
      console.error('Error fetching payment information:', error)
    }
  }

  useEffect(() => {
    requestGenericPremiumOnBidMountChange(formik.values.BidAmount, formik.values?.BiddingDealerID)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.BidAmount, formik.values?.BiddingDealerID])

  useEffect(() => {
    setSelectedDeliveryPrice(null)
    if (isOther && !!otherChildId) {
      const price =
        deliveryInformation?.VehicleDeliveryQuotations?.DiscountedPrice || deliveryInformation?.DiscountedPrice
      setSelectedDeliveryPrice(price)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOther, otherChildId, deliveryInformation?.VehicleDeliveryQuotations, setSelectedDeliveryPrice])

  // Reset transportation selection when bidding vehicle changes
  useEffect(() => {
    setHasTransportationSelected(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [biddingVehicleInstanceId])

  const fetchVehicleInformation = async (biddingDealerID?: number) => {
    const payload = new GetBaseVehicleRequest([biddingVehicle])
    payload.IncludeAuctionData = true
    payload.IncludeBaseData = true
    payload.IncludeInspectionReport = false
    const {
      Items: [vehicleData]
    } = await getBaseVehicle(payload, biddingDealerID)

    // Update both local state and global store
    setVehicleInfo(vehicleData.vehicle)

    formik.setFieldValue(
      'BuyerAccountReference',
      vehicleData.accountList.find((account) => account.ID === vehicleData.curAccount)?.Description ?? '',
      false
    )
    updateVehicleData(vehicleData)

    if (isPurchaseExclusive) {
      setBuyerFee(vehicleData.vehicle.buyerFee || 0)
      setBuyerFeeString(vehicleData.vehicle.buyerFeeString || 'N/A')
    }
  }

  const handleDealerChange = async (dealershipId?: number) => {
    // Prevent multiple simultaneous calls
    if (isDealerChangeInProgress) {
      return
    }

    if (biddingVehicle?.auctionInfo?.BuyerID === CurrentBuyerID) {
      dealershipId = dealershipId || biddingVehicle.auctionInfo?.BuyerID
    } else {
      dealershipId = dealershipId || CurrentBuyerID
    }

    try {
      setIsDealerChangeInProgress(true)
      setFetchLoading(true)
      setSelectedDeliveryPrice(null)
      setIsOther(false)
      setOtherChildId(null)
      setDeliveryInformation(null)
      setHasTransportationSelected(false)
      if (isPurchaseExclusive) {
        await Promise.all([fetchVehicleInformation(dealershipId), fetchPaymentInfo(biddingVehicle.vehicle.InstanceID)])
      } else {
        await Promise.all([fetchVehicleInformation(dealershipId), fetchDetailedDeliveryInfo(dealershipId)])
      }
    } catch (error) {
      console.error(error)
    } finally {
      setFetchLoading(false)
      setIsDealerChangeInProgress(false)
    }
  }

  // Ensure handleDealerChange is called when component mounts with isPurchaseExclusive
  // and vehicleInfo is not available (for session-based initialization)
  useEffect(() => {
    if (isPurchaseExclusive && biddingVehicle && !vehicleInfo && !isDealerChangeInProgress) {
      handleDealerChange()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPurchaseExclusive, biddingVehicle, vehicleInfo, isDealerChangeInProgress])

  useSidePanelRealTimeUpdateDealer(formik, handleDealerChange)

  const requestGenericPremiumOnBidMountChange = debounce(async (bidAmount: number, biddingDealerId: number) => {
    if (hasAnyChargeRange) {
      try {
        const feeRequest: IVehicleFeeRequest = {
          VehicleInstanceId: biddingVehicle?.vehicle.InstanceID,
          LocationId: biddingVehicle?.vehicle.LocationID,
          SaleChannelId: biddingVehicle?.vehicle.SaleChannelID,
          Price: bidAmount
        }
        setFeeLoading(true)
        const feeResponse = await getGenericPremium(feeRequest, biddingDealerId)

        setHasAnyChargeRange(feeResponse.HasAnyChargeRange)
        setBuyerFee(feeResponse.Premium)
        setBuyerFeeString(feeResponse.PremiumString)
      } catch (error) {
        console.error(error)
      } finally {
        setFeeLoading(false)
      }
    }
  }, 300)

  const handleTransportationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    formik.handleChange(event)
    setHasTransportationSelected(true)
    const value = event.target.value
    const transportProviderID = value === 'other' ? OTHER_TRANSPORT_PROVIDER_ID : SELF_ARRANGED_TRANSPORT_PROVIDER_ID
    formik.setFieldValue('TransportCompanyID', transportProviderID)
    if (value === 'other') {
      openTransportationSlideOut()
      setIsOther(true)
      const currentPrice =
        deliveryInformation?.VehicleDeliveryQuotations?.DiscountedPrice || deliveryInformation?.DiscountedPrice
      setSelectedDeliveryPrice(currentPrice)
    } else {
      setIsOther(false)
      closeTransportationSlideOut()
      setSelectedDeliveryPrice(null)
      formik.setFieldValue('DeliveryAddressID', -1)
    }
  }

  const handlePaymentChange = (paymentTypeId?: number) => {
    setHasPaymentMethodSelected(paymentTypeId && paymentTypeId !== 0)
  }

  return (
    <>
      <SlideOutHeading vehicleTitle={biddingVehicle?.vehicle.Title} vin={biddingVehicle?.vehicle.VIN}></SlideOutHeading>
      <SlideOutDropdown
        required
        label="Payment Method"
        options={paymentTypes}
        fallbackValue={paymentTypes?.[0]}
        name="PaymentTypeID"
        disabled={(isBidSuccess && isBuyItNow) || (isBidSuccess && isPurchaseExclusive)}
        onChange={handlePaymentChange}
      ></SlideOutDropdown>

      <SlideOutDropdown
        // alertMessage={
        //   formik.values.BiddingDealerID !== biddingVehicle?.auctionInfo?.BuyerID && !isPurchaseExclusive && !isBuyItNow
        //     ? `By selecting "Current Bid Amount" and clicking "Update", you'll be transferring your previously entered bid to the selected dealership.\nBy selecting "Update Bid Amount" and clicking "Update", you'll be creating a new bid.`
        //     : null
        // }
        disabled={!isDealerShipSelectable}
        label={isBuyItNow || isPurchaseExclusive ? 'Purchasing Dealership' : 'Bidding Dealership'}
        onChange={handleDealerChange}
        options={dealershipOptions}
        fallbackValue={CurrentBuyerID}
        name="BiddingDealerID"
      ></SlideOutDropdown>

      {deliveryInformation?.CurrentBuyerAlternateIdentifiers?.length > 1 && !(isBidSuccess && isPurchaseExclusive) && (
        <SlideOutDropdown
          label="Buyer Account"
          options={buyerAccountOptions}
          fallbackValue={
            deliveryInformation?.CurrentBuyerAlternateIdentifiers?.find(
              (account) => account.Description === deliveryInformation?.CurrentBuyerAccountRef
            )?.Description
          }
          name="BuyerAccountReference"
        ></SlideOutDropdown>
      )}
      {!isPurchaseExclusive && !(isBidSuccess && isBuyItNow) && (
        <StyledSlideOutElement>
          <Form.Group controlId="transportation-provider">
            <Form.Label className="mb-4">Transportation Provider</Form.Label>
            <div className="d-flex">
              {isExistOtherTransportProvider && (
                <CustomRadioButton
                  id="other-transportation"
                  label="Other"
                  value="other"
                  disabled={isFetchingDeliveryInfo || !isExistOtherTransportProvider}
                  checked={hasTransportationSelected && !isFetchingDeliveryInfo && isOther}
                  onChange={(e) => handleTransportationChange(e)}
                />
              )}
              <CustomRadioButton
                id="self-arrange-transportation"
                label={selfArrangeTransportationProvider?.Description || 'Self-Arranged'}
                value={selfArrangeTransportationProvider?.ID}
                disabled={isFetchingDeliveryInfo}
                checked={
                  (hasTransportationSelected ||
                    (biddingVehicle?.auctionInfo?.HasPreviousBid &&
                      biddingVehicle?.auctionInfo?.BuyerID === CurrentBuyerID)) &&
                  !isFetchingDeliveryInfo &&
                  !isOther
                }
                onChange={(e) => handleTransportationChange(e)}
              />
            </div>
          </Form.Group>
        </StyledSlideOutElement>
      )}

      {/* bid again */}
      {biddingVehicle?.auctionInfo?.HasPreviousBid ? (
        <>
          <StyledSlideOutBidAmountInput>
            <Form.Check
              name="BidAmountType"
              type="radio"
              id="current-bid-amount"
              label="Current Bid Amount"
              value={BidAmountTypeOptions.CurrentBidAmount}
              checked={formik.values.BidAmountType === BidAmountTypeOptions.CurrentBidAmount}
              onChange={(event) => formik.setFieldValue('BidAmountType', event.target.value)}
            />
            <Form.Control
              className="read-only-input"
              type="text"
              readOnly
              placeholder={normalizeCurrencySymbol(biddingVehicle?.auctionInfo?.UserMaxBidString)}
              disabled={formik.values.BidAmountType !== BidAmountTypeOptions.CurrentBidAmount}
            />
          </StyledSlideOutBidAmountInput>

          <StyledSlideOutElement>
            <Form.Check
              name="BidAmountType"
              type="radio"
              id="update-bid-amount"
              label="Update Bid Amount"
              checked={formik.values.BidAmountType === BidAmountTypeOptions.UpdateBidAmount}
              value={BidAmountTypeOptions.UpdateBidAmount}
              onChange={(event) => formik.setFieldValue('BidAmountType', event.target.value)}
            />

            <SlideOutBidAmountInput
              disabled={formik.values.BidAmountType !== BidAmountTypeOptions.UpdateBidAmount}
              nextBidAmount={biddingVehicle?.auctionInfo?.NextBidAmount}
              bidIncreasement={biddingVehicle?.auctionInfo?.BidIncrement}
              bidIncreasementString={biddingVehicle?.auctionInfo?.BidIncrementString}
              nextBidAmountString={biddingVehicle?.auctionInfo?.NextBidAmountString}
            ></SlideOutBidAmountInput>
          </StyledSlideOutElement>
        </>
      ) : isBuyItNow || isPurchaseExclusive ? (
        <></>
      ) : (
        // first bid
        <StyledSlideOutElement>
          <Form.Group controlId="bid-again">
            <Form.Label>Enter Bid Amount</Form.Label>
            <SlideOutBidAmountInput
              bidIncreasement={biddingVehicle?.auctionInfo?.BidIncrement}
              bidIncreasementString={biddingVehicle?.auctionInfo?.BidIncrementString}
              nextBidAmountString={biddingVehicle?.auctionInfo?.NextBidAmountString}
              nextBidAmount={biddingVehicle?.auctionInfo?.NextBidAmount}
            ></SlideOutBidAmountInput>
          </Form.Group>
        </StyledSlideOutElement>
      )}

      <SlideOutPricingDetails
        feeLoading={feeLoading}
        buyerFeeString={normalizeCurrencySymbol(buyerFeeString) as string}
        relistFeeString={vehicleInfo?.reListFeeString as string}
        deliveryCharge={selectedDeliveryPrice}
        vehicleCharges={vehicleInfo?.Charges}
        isIncentiveAccumulate={vehicleInfo?.IsIncentiveAccumulate}
        incentives={vehicleInfo?.Incentives}
        estimatedGrossPrice={
          !isBuyItNow && !isPurchaseExclusive
            ? estimatedGrossPrice + (formik.values.BidAmount - biddingVehicle?.auctionInfo?.NextBidAmount)
            : estimatedGrossPrice
        }
        hasAnyChargeRange={hasAnyChargeRange}
        buyItNowPriceString={normalizeCurrencySymbol(biddingVehicle?.vehicle.BuyItNowPriceString)}
        purchaseExclusivePriceString={normalizeCurrencySymbol(biddingVehicle?.vehicle.FinalPriceString)}
      ></SlideOutPricingDetails>
    </>
  )
}
